import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { MarkdownRenderer } from './MarkdownRenderer';

interface PrivacyNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyNoticeModal: React.FC<PrivacyNoticeModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load privacy notice content from public folder
  useEffect(() => {
    const loadPrivacyNotice = async () => {
      if (!isOpen || privacyContent) return; // Only load once when opened
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch('/privacy-notice.md');
        if (!response.ok) {
          throw new Error(`Failed to load privacy notice: ${response.statusText}`);
        }
        const text = await response.text();
        setPrivacyContent(text);
      } catch (err) {
        console.error('Error loading privacy notice:', err);
        setError('Failed to load privacy notice. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPrivacyNotice();
  }, [isOpen, privacyContent]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative w-[90vw] max-w-4xl h-[90vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col">
        {/* Header with title and close button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              Privacy Notice
            </h2>
          </div>
          <div className="flex items-center ml-4">
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
              title="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading privacy notice...</div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-600">{error}</div>
            </div>
          )}
          
          {!isLoading && !error && privacyContent && (
            <div className="max-w-3xl mx-auto">
              <MarkdownRenderer 
                content={privacyContent}
                className="text-sm"
              />
            </div>
          )}
        </div>
        
        {/* Footer with close button */}
        <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyNoticeModal;
